import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiClient from '../api/ApiClient';

const illegalWasteDumpsAPI = createSlice({
	name: 'illegal-waste-dumps',
	initialState: {
		list: [],
		mapList: [],
		details: {},
		history: [],
		municipalities: [],
		ownershipTypes: [],
		dedicatedLandUses: [],
		cadastralMunicipalities: [],
		actualLandUses: [],
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchIllegalWasteDumpsPage.fulfilled, (state, action) => {
				state.list = action.payload;
			})
			.addCase(fetchIllegalWasteDumpsDetails.fulfilled, (state, action) => {
				state.details = action.payload;
			})
			.addCase(
				fetchIllegalWasteDumpsEditHistoryPage.fulfilled,
				(state, action) => {
					state.history = action.payload;
				}
			)
			.addCase(fetchMunicipalities.fulfilled, (state, action) => {
				state.municipalities = action.payload;
			})
			.addCase(fetchOwnershipTypes.fulfilled, (state, action) => {
				state.ownershipTypes = action.payload;
			})
			.addCase(fetchDedicatedLandUses.fulfilled, (state, action) => {
				state.dedicatedLandUses = action.payload;
			})
			.addCase(fetchCadastralMunicipalities.fulfilled, (state, action) => {
				state.cadastralMunicipalities = action.payload;
			})
			.addCase(fetchActualLandUses.fulfilled, (state, action) => {
				state.actualLandUses = action.payload;
			})
		.addCase(fetchIllegalWasteDumpsPageForMap.fulfilled, (state, action) => {
			state.mapList = action.payload;
		});
	},
});

export const fetchIllegalWasteDumpsPage = createAsyncThunk(
	'illegal-waste-dumps/fetch/page',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export const fetchIllegalWasteDumpsPageForMap = createAsyncThunk(
	'illegal-waste-dumps/map/fetch/page',
	async ({ filter }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/map`,
			null,
			null,
			filter,
			null
		);
		return response.data;
	}
);

export const fetchIllegalWasteDumpsDetails = createAsyncThunk(
	'illegal-waste-dumps/fetch/details',
	async (id) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/${id}`
		);
		return response.data;
	}
);

export const updateIllegalWasteDumpsDetails = createAsyncThunk(
	'illegal-waste-dumps/mutate/update',
	async ({ illegalWasteDumpsId, dto }, { dispatch }) => {
		const response = await apiClient.put(
			`/api/illegal-dumping-location/web/${illegalWasteDumpsId}`,
			dto
		);
		return response.data;
	}
);

export const deleteIllegalDumpingLocation = createAsyncThunk(
	'illegal-waste-dumps/mutate/delete',
	async ({ illegalWasteDumpsId }, { dispatch }) => {
		const response = await apiClient.delete(
			`/api/illegal-dumping-location/web/${illegalWasteDumpsId}`
		);
		return response.data;
	}
);

export const fetchIllegalWasteDumpsEditHistoryPage = createAsyncThunk(
	'illegal-waste-dumps/fetch/history',
	async ({ id, params }) => {
		const page = params?.page || 0;
		const filter = params?.filter || 'createdAt';
		const sort = params?.filter || 'DESC';
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/${id}/history`,
			null,
			page,
			filter,
			sort
		);

		if (response.data.content?.length > 0) {
			const result = response.data.content
				.filter((entry) => entry !== null)
				.map((entry, index) => {
					const currentData = {};
					const oldData = {};

					for (const key in entry) {
						if (
							entry[key] &&
							typeof entry[key] === 'object' &&
							'old' in entry[key] &&
							'current' in entry[key]
						) {
							currentData[key] = entry[key].current || null;
							oldData[key] = entry[key].old || null;
						}
					}

					return {
						id: index,
						changedBy: entry['changedBy'] || null,
						changedAt: entry['changedAt'] || null,
						current: currentData,
						old: oldData,
					};
				});
			response.data.content = result;
		}

		return response.data;
	}
);

export const fetchMunicipalities = createAsyncThunk(
	'illegal-waste-dumps/fetch/municipalities',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/municipalities`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export const fetchOwnershipTypes = createAsyncThunk(
	'illegal-waste-dumps/fetch/ownership-types',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/ownership-types`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export const fetchDedicatedLandUses = createAsyncThunk(
	'illegal-waste-dumps/fetch/dedicated-land-uses',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/dedicated-land-uses`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export const fetchCadastralMunicipalities = createAsyncThunk(
	'illegal-waste-dumps/fetch/cadastral-municipalities',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/cadastral-municipalities`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export const fetchActualLandUses = createAsyncThunk(
	'illegal-waste-dumps/fetch/actual-land-uses',
	async ({ page, filter, sort }) => {
		const response = await apiClient.get(
			`/api/illegal-dumping-location/web/actual-land-uses`,
			null,
			page,
			filter,
			sort
		);
		return response.data;
	}
);

export default illegalWasteDumpsAPI.reducer;
